import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";

import { useCheckIsMobile } from "@sellernote/shared/src/utils/hook";
import useOnceActivatedIntersectionObserver from "@sellernote/features/src/common/hooks/useOnceActivatedIntersectionObserver";
import Tab, { TabDataItem } from "@sellernote/ui/src/sds-v2/components/tab/Tab";
import { COLOR } from "@sellernote/ui/src/sds-v2/styles/colors";

import CommonStyled from "../index.styles";
import Styled from "./index.styles";

export type AnimatedImageTabData<T> = TabDataItem<T> & {
  webmUrl: string;
  mp4Url: string;
};

export type ImageTabData<T> = TabDataItem<T> & {
  imageSrc: string;
};

export default function LandingSectionTabSlider<T extends string>({
  title,
  ...propsByIsAnimatedImage
}: {
  title: ReactNode;
} & (
  | {
      tabDataList: AnimatedImageTabData<T>[];
      isAnimatedImage: true;
    }
  | {
      tabDataList: ImageTabData<T>[];
      isAnimatedImage?: false;
    }
)) {
  const { tabDataList, isAnimatedImage = false } = propsByIsAnimatedImage;

  const videoRef = useRef<HTMLVideoElement[]>([]);

  const { isMobile } = useCheckIsMobile();

  const { target, isActivatedTarget } = useOnceActivatedIntersectionObserver({
    threshold: 0.3,
  });

  const [selectedTab, setSelectedTab] = useState<T>(tabDataList[0].tabValue);

  const currentIndex = tabDataList
    .map(({ tabValue }) => tabValue)
    .indexOf(selectedTab);

  const handleTabSelect = useCallback(
    (selectedTabValue: T) => {
      setSelectedTab(selectedTabValue);

      if (isAnimatedImage) {
        const selectedIndex = tabDataList
          .map(({ tabValue }) => tabValue)
          .indexOf(selectedTabValue);

        if (videoRef.current[selectedIndex]) {
          videoRef.current[selectedIndex].currentTime = 0;
        }
      }
    },
    [isAnimatedImage, tabDataList]
  );

  useEffect(() => {
    if (isMobile) return;

    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % tabDataList.length;

      handleTabSelect(tabDataList[nextIndex].tabValue);
    }, 2000);

    return () => clearInterval(interval);
  }, [tabDataList, currentIndex, isMobile, selectedTab, handleTabSelect]);

  useEffect(() => {
    if (isAnimatedImage && isActivatedTarget) {
      videoRef.current.forEach((video) => {
        video.load();
      });
    }
  }, [isActivatedTarget, isAnimatedImage]);

  const setVideoRef = useCallback(
    (index: number) => (ref: HTMLVideoElement | null) => {
      if (ref) {
        videoRef.current[index] = ref;
      }
    },
    []
  );

  return (
    <Styled.container ref={target}>
      <CommonStyled.sectionTitle
        textColor={COLOR.primary_600}
        className={`${isMobile ? "fade-up-animation" : ""} ${
          isActivatedTarget ? "active" : ""
        }`}
      >
        {title}
      </CommonStyled.sectionTitle>

      <Styled.contentsContainer>
        <div
          className={`tab-wrapper ${isMobile ? "fade-up-animation" : ""} ${
            isActivatedTarget ? "active" : ""
          }`}
        >
          <Tab<T>
            uiType="box"
            direction={isMobile ? "horizontal" : "vertical"}
            align="right"
            selectedTab={selectedTab}
            onSelect={handleTabSelect}
            tabDataList={tabDataList}
            className="custom-tab"
          />
        </div>

        <div
          className={`image-container ${isMobile ? "fade-up-animation" : ""} ${
            isActivatedTarget ? "active" : ""
          }`}
        >
          {tabDataList.map((item, index) => (
            <div
              key={item.tabValue}
              className={`image-wrapper ${
                item.tabValue === selectedTab ? "active" : ""
              }`}
            >
              {isAnimatedImage ? (
                <Styled.video
                  ref={setVideoRef(index)}
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="/assets/images/landing/section-image-blur-data.png"
                >
                  <source
                    src={
                      isActivatedTarget
                        ? (item as AnimatedImageTabData<T>).webmUrl
                        : ""
                    }
                    type="video/webm"
                  />
                  <source
                    src={
                      isActivatedTarget
                        ? (item as AnimatedImageTabData<T>).mp4Url
                        : ""
                    }
                    type="video/mp4"
                  />
                  Sorry, your browser doesn't support embedded videos.
                </Styled.video>
              ) : (
                <Image
                  src={(item as ImageTabData<T>).imageSrc}
                  alt={item.tabValue}
                  width={isMobile ? 658 : 800}
                  height={isMobile ? 368 : 448}
                  placeholder="blur"
                  blurDataURL="/assets/images/landing/section-image-blur-data.png"
                />
              )}
            </div>
          ))}
        </div>
      </Styled.contentsContainer>

      <Styled.quoteContainer>
        <div className="quote-left-wrapper">
          <Image
            src={`/assets/images/landing/quote-left.png`}
            alt=""
            width={isMobile ? 117 : 176.5}
            height={isMobile ? 72 : 131.5}
          />
        </div>

        <div className="quote-right-wrapper">
          <Image
            src={`/assets/images/landing/quote-right.png`}
            alt=""
            width={isMobile ? 100.5 : 126}
            height={isMobile ? 57.5 : 87}
          />
        </div>
      </Styled.quoteContainer>
    </Styled.container>
  );
}
